import type { LucideIcon } from "lucide-react";

interface Feature {
  icon: LucideIcon;
  title: string;
  description: string;
}

interface AuthHeroSectionProps {
  brandIcon: LucideIcon;
  brandTitle: string;
  mainTitle: string;
  highlightedTitle: string;
  description: string;
  features: Feature[];
  colorScheme?: "primary" | "success";
}

// LiveCargo brand SVG mark
const BrandMarkSVG = () => (
  <svg
    width="56"
    height="56"
    viewBox="0 0 100 100"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect x="22" y="38" width="2" height="24" fill="#C97A3C" opacity="0.5" />
    <rect x="27" y="34" width="2" height="32" fill="#C97A3C" opacity="0.6" />
    <rect x="32" y="30" width="3" height="40" fill="#C97A3C" opacity="0.7" />
    <rect x="38" y="24" width="3" height="52" fill="#C97A3C" opacity="0.85" />
    <rect x="44" y="18" width="4" height="64" fill="#E89D5C" />
    <rect x="51" y="24" width="3" height="52" fill="#C97A3C" opacity="0.85" />
    <rect x="57" y="30" width="3" height="40" fill="#C97A3C" opacity="0.7" />
    <rect x="63" y="34" width="2" height="32" fill="#C97A3C" opacity="0.6" />
    <rect x="68" y="38" width="2" height="24" fill="#C97A3C" opacity="0.5" />
  </svg>
);

export function AuthHeroSection({
  mainTitle,
  highlightedTitle,
  description,
  features,
}: AuthHeroSectionProps) {
  return (
    <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden bg-[#0B0F0E]">
      {/* Copper radial glow — top right */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: "-150px",
          right: "-150px",
          width: "460px",
          height: "460px",
          background: "#C97A3C",
          borderRadius: "50%",
          filter: "blur(180px)",
          opacity: 0.4,
        }}
      />
      {/* Secondary subtle glow — bottom left */}
      <div
        className="absolute pointer-events-none"
        style={{
          bottom: "-180px",
          left: "-120px",
          width: "440px",
          height: "440px",
          background: "#E89D5C",
          borderRadius: "50%",
          filter: "blur(200px)",
          opacity: 0.12,
        }}
      />

      <div className="relative z-10 flex flex-col justify-between px-12 xl:px-20 py-16 text-white w-full">
        {/* Logo / brand header */}
        <div className="flex items-center gap-1">
          <BrandMarkSVG />
          <span
            style={{
              fontSize: "26px",
              fontWeight: 500,
              letterSpacing: "-0.8px",
              lineHeight: 1,
            }}
            className="text-[#E89D5C]"
          >
            LiveCargo<span className="text-[#C97A3C]">.</span>
          </span>
        </div>

        {/* Main copy */}
        <div>
          <h2
            className="mb-6 leading-tight font-medium"
            style={{
              fontSize: "36px",
              letterSpacing: "-1.1px",
              maxWidth: "380px",
            }}
          >
            {mainTitle}
            <br />
            <span className="text-[#E89D5C]">{highlightedTitle}</span>
          </h2>

          <p className="text-[#8A9590] text-base leading-relaxed mb-12 max-w-sm">
            {description}
          </p>

          {/* Features */}
          <div className="space-y-5">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center gap-4">
                <div className="h-10 w-10 bg-white/10 rounded-lg flex items-center justify-center shrink-0">
                  <feature.icon className="h-5 w-5 text-[#E89D5C]" />
                </div>
                <div>
                  <h3 className="font-medium text-white text-sm">
                    {feature.title}
                  </h3>
                  <p className="text-[#8A9590] text-xs leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer note */}
        <p className="text-[#8A9590] text-xs">
          © {new Date().getFullYear()} LiveCargo · by Live Technologies
        </p>
      </div>
    </div>
  );
}
